import {
  Feedback as BaseFeedback,
  Message as BaseMessage,
  FeedbackOnMessage,
  MessageMode,
} from "@prisma/client"
import {last, reverse, sortBy} from "lodash"
import {z} from "zod"

import {CauseOnMessageWithCauseAndPart} from "../causeOnMessage/client"
import {Feedback} from "../feedback"
import {findLastMap} from "../findMap"

export const ASK_FOR_MODEL_NUMBER: typeof MessageMode.ASK_FOR_MODEL_NUMBER =
  "ASK_FOR_MODEL_NUMBER"

export const MessageImage = z.object({
  url: z.string(),
  title: z.string().nullish(),
})

export type MessageImage = z.infer<typeof MessageImage>

const getHumanMessageTextFromLegacyPart = (part: unknown): string => {
  if (typeof part === "string") {
    return part
  }

  if (!part || typeof part !== "object") {
    return ""
  }

  if ("text" in part) {
    const {text} = part
    if (typeof text === "string") {
      return text
    }
    if (
      text &&
      typeof text === "object" &&
      "value" in text &&
      typeof text.value === "string"
    ) {
      return text.value
    }
  }

  if ("content" in part && typeof part.content === "string") {
    return part.content
  }

  return ""
}

const normalizeHumanMessageText = (content: string | unknown[]): string =>
  typeof content === "string"
    ? content
    : content
        .map(getHumanMessageTextFromLegacyPart)
        .filter((part) => part.length > 0)
        .join("\n\n")

export const HumanMessageContent = z.object({
  // Some older rows store rich content blocks instead of a plain string.
  content: z
    .union([z.string(), z.array(z.unknown())])
    .transform(normalizeHumanMessageText),
  images: z.array(MessageImage).optional(),
  answers: z
    .tuple([z.number(), z.number()])
    .array()
    .default([])
    .transform((arr) => new Map(arr)),
  isMultipleChoiceAnswer: z.boolean().default(false),
  isHidden: z.boolean().optional(),
})
export type HumanMessageContent = z.infer<typeof HumanMessageContent>

export type HumanMessage = {
  type: "human"
  content: HumanMessageContent
  mode: null
}

export const MessageVideo = z.object({
  url: z.string(),
  thumbnailUrl: z.string(),
  title: z.string().nullish(),
})

export type MessageVideo = z.infer<typeof MessageVideo>

export type MessageMedia = MessageImage | MessageVideo

const MessagePartImages = z.object({
  partType: z.string(),
  images: z.array(MessageImage),
})

export type MessagePartImages = z.infer<typeof MessagePartImages>

export const QuestionDocument = z.object({
  category: z.string(),
  name: z.string(),
  url: z.string(),
  thumbnailUrl: z.string().optional(),
})
export type QuestionDocument = z.infer<typeof QuestionDocument>

export const MultipleChoiceAnswer = z.object({
  text: z.string(),
  decisionTreeAnswerId: z.number().optional(),
  causeIdsToConfirm: z.string().array().optional(),
  causeIdsToRuleOut: z.string().array().optional(),
})
export type MultipleChoiceAnswer = z.infer<typeof MultipleChoiceAnswer>

export const ConfirmedCompatiblePart = z.object({
  id: z.number(),
  name: z.string().nullable(),
  description: z.string().nullable().optional(),
  manufacturerPartNumber: z.string(),
  manufacturerName: z.string().nullable().optional(),
  price: z.number().nullable(),
  purchaseUrl: z.string().nullable(),
  image: MessageImage.nullable(),
  isNoLongerAvailable: z.boolean(),
  stockStatus: z.string().nullable().optional(),
})

export type ConfirmedCompatiblePart = z.infer<typeof ConfirmedCompatiblePart>

export const AIMessageContent = z.object({
  question: z.string(),
  workflowCategory: z
    .enum(["DIAGNOSIS", "ECOMMERCE", "CUSTOMER_SERVICE", "UNSUPPORTED"])
    .optional(),
  caseName: z.string().optional(),
  summary: z.string().optional(),
  decisionTreeQuestionId: z.number().optional(),
  explanation: z.string().optional(),
  askedDiagnosticQuestion: z.boolean().optional(),
  multipleChoiceAnswers: z.optional(
    z.union([
      MultipleChoiceAnswer.array(),
      // for backwards compatibility
      z.string().array(),
    ]),
  ),
  questionImages: z.optional(z.array(MessageImage)),
  questionVideos: z.optional(z.array(MessageVideo)),
  helperQuestionImages: z.optional(z.array(MessageImage)),
  helperQuestionVideos: z.optional(z.array(MessageVideo)),
  questionDocuments: z.optional(z.array(QuestionDocument)),
  disassemblyVideos: z.optional(z.array(MessageVideo)),
  partImages: z.optional(MessagePartImages),
  partTestingVideos: z.optional(z.array(MessageVideo)),
  generalKnowledgeSummary: z.string().optional(),
  confirmedPart: z.optional(ConfirmedCompatiblePart),
  confirmedParts: z.optional(z.array(ConfirmedCompatiblePart)),
  suggestedParts: z.optional(
    z.array(
      z.object({
        part: ConfirmedCompatiblePart,
        causeName: z.string(),
        probability: z.number(),
      }),
    ),
  ),
  generationDurationMs: z.number().optional(),
})

export type AIMessageContent = z.infer<typeof AIMessageContent>

export type AIMessage = {
  type: "ai"
  content: AIMessageContent
  mode: MessageMode | null
  causes: CauseOnMessageWithCauseAndPart[]
  feedback?: Feedback
}

export type Message = Pick<
  BaseMessage,
  "id" | "caseId" | "createdAt" | "basedOnMessageId"
> &
  (AIMessage | HumanMessage)

export type BaseMessageWithCausesAndFeedback = BaseMessage & {
  causes: CauseOnMessageWithCauseAndPart[]
  feedbackOnMessages: (FeedbackOnMessage & {feedback: BaseFeedback})[]
}

export const isHumanMessage = (
  message: Message,
): message is Pick<
  BaseMessage,
  "id" | "caseId" | "createdAt" | "basedOnMessageId"
> &
  HumanMessage => message.type === "human"

export const isAIMessage = (
  message: Message,
): message is Pick<
  BaseMessage,
  "id" | "caseId" | "createdAt" | "basedOnMessageId"
> &
  AIMessage => message.type === "ai"

export const getCaseName = (
  messages: (
    | Pick<AIMessage, "type" | "content">
    | Pick<HumanMessage, "type">
  )[],
): string => getCaseNameIfMessages(messages) || "New Case"

export const getCaseNameIfMessages = (
  messages: (
    | Pick<AIMessage, "type" | "content">
    | Pick<HumanMessage, "type">
  )[],
): string | null =>
  findLastMap(messages, (message) =>
    message.type === "ai" ? message.content.caseName : undefined,
  ) || null

export const getCausesForMessage = (
  message: Message,
): CauseOnMessageWithCauseAndPart[] =>
  isAIMessage(message) ? message.causes : []

export const getCausesFromMessages = (
  messages: Message[],
): CauseOnMessageWithCauseAndPart[] => {
  const sortedMessages = reverse(sortBy(messages, (m) => m.createdAt))
  for (const message of sortedMessages) {
    const causes = getCausesForMessage(message)
    if (causes.length) return causes
  }
  return []
}

export const getContentFromMessage = (message: Message): string =>
  isAIMessage(message) ? message.content.question : message.content.content

export const getContentFromLastMessage = (
  messages: Message[],
): string | null => {
  const lastMsg = last(messages)

  if (!lastMsg) return null

  return getContentFromMessage(lastMsg)
}

export const getMediaFromMessage = (message: Message): MessageMedia[] => {
  if (isAIMessage(message)) {
    const questionDocsAsImages: MessageImage[] = (
      message.content.questionDocuments ?? []
    ).map((doc) => ({url: doc.url, title: doc.name}))
    return [
      ...(message.content.questionImages ?? []),
      ...(message.content.questionVideos ?? []),
      ...(message.content.helperQuestionImages ?? []),
      ...(message.content.helperQuestionVideos ?? []),
      ...(message.content.partTestingVideos ?? []),
      ...(message.content.disassemblyVideos ?? []),
      ...(message.content.partImages?.images ?? []),
      ...questionDocsAsImages,
    ]
  } else if (isHumanMessage(message)) {
    return message.content.images ?? []
  }
  return []
}

export const isMessageVideo = (media: MessageMedia): media is MessageVideo =>
  (media as MessageVideo).thumbnailUrl !== undefined
